/**
 * 엑셀 내려받기
 * ---------------------------------------------------------------------------
 * ## 왜 CSV 인가
 * xlsx 를 만들려면 라이브러리가 필요하고(압축된 XML 묶음이다) 번들이 수백 KB
 * 늘어난다. 내보내는 자료는 화면에 뜬 표 하나뿐이라 엑셀이 그대로 여는 CSV 로
 * 충분하다.
 *
 * ## BOM 을 반드시 붙인다
 * 엑셀은 BOM 이 없는 UTF-8 CSV 를 **시스템 기본 인코딩(한국어 윈도는 CP949)**
 * 으로 읽는다. 그래서 한글이 전부 깨진다. 앞에 `﻿` 를 한 글자 붙이면
 * 엑셀이 UTF-8 로 알아본다 — 이것 하나로 갈린다.
 *
 * ## 숫자를 문자열로 감싸지 않는다
 * 측정값을 `"1,234"` 로 내보내면 엑셀이 글자로 읽어 합계가 되지 않는다.
 * 숫자는 쉼표 없이 그대로 쓴다. 보기 좋게 만드는 것은 받는 쪽의 서식이다.
 *
 * ## 왜 필요한가
 * 운영일지는 법정 기록이고, 서비스를 그만두더라도 고객이 가져갈 수 있어야
 * 한다(클라우드컴퓨팅법 제27조의 이용자 정보 반환). 화면에서 바로 받을 수
 * 있으면 그 의무를 평소에 충족한다.
 */

export interface CsvColumn<T> {
  header: string;
  /** 값. 숫자를 돌려주면 엑셀에서 숫자로 들어간다 */
  value: (row: T) => string | number | null | undefined;
}

/**
 * 엑셀이 수식으로 읽어 버리는 첫 글자.
 *
 * `=`, `+`, `-`, `@` 로 시작하는 칸을 엑셀은 **값이 아니라 수식**으로 본다.
 * 비고나 시설명에 `=HYPERLINK(...)` 같은 글자를 넣어 두면, 그 표를 받아 연
 * 사람의 엑셀에서 그대로 실행된다(CSV 수식 주입). 우리가 쓰는 칸 가운데
 * 사업장명·시설명·제목·측정대행업체·파일명·비고가 모두 사람이 적는 값이다.
 *
 * 탭과 캐리지리턴도 같은 취급을 받는 자리가 있어 함께 막는다.
 */
const FORMULA_START = /^[=+\-@\t\r]/;

/**
 * 한 칸을 CSV 규칙에 맞게 감싼다.
 *
 * 쉼표·따옴표·줄바꿈이 들어 있으면 따옴표로 감싸고, 안의 따옴표는 두 번
 * 적는다. 사업장 이름이나 비고에 쉼표가 들어가는 일은 흔하다.
 *
 * <b>숫자는 건드리지 않는다.</b> 음수(-5)는 숫자 칸으로 들어오므로 아래
 * 수식 방어에 걸리지 않는다. 글자로 들어온 `-5` 만 따옴표가 하나 붙는데,
 * 그 편이 남의 엑셀에서 수식이 도는 것보다 낫다.
 */
function cell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';

  // 수식으로 읽힐 첫 글자면 작은따옴표를 앞에 붙인다. 엑셀은 이것을
  // "이 칸은 글자다" 로 알아보고, 화면에는 따옴표를 보여 주지 않는다.
  const raw = String(value);
  const text = FORMULA_START.test(raw) ? `'${raw}` : raw;

  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T>(rows: T[], columns: Array<CsvColumn<T>>): string {
  const head = columns.map((c) => cell(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => cell(c.value(r))).join(','));
  return [head, ...body].join('\r\n');
}

/**
 * 내려받게 한다.
 *
 * 파일명에 날짜를 넣는다. 「운영일지.csv」가 내려받기 폴더에 여러 개 쌓이면
 * 어느 것이 무엇인지 알 수 없다.
 */
export function downloadCsv<T>(
  fileName: string,
  rows: T[],
  columns: Array<CsvColumn<T>>,
): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const csv = `﻿${toCsv(rows, columns)}`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}_${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // 바로 지우면 사파리에서 내려받기가 끊긴다. 한 틱 뒤에 치운다.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** 날짜 칸. 엑셀이 날짜로 알아보도록 YYYY-MM-DD 로 적는다 */
export function csvDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
